import User from "@/models/User";

const PURCHASE_EVENT_TYPES = ["subscribed", "sale", "bf_sale"];

// The event's Hindsight session ID, or null at once if the page is hidden or
// left while it is pending, so the event is sent before the page goes. The
// listeners capture, to run before the page's other pagehide listeners.
function sessionIdUnlessLeaving(pending) {
  return new Promise((resolve) => {
    const settle = (sessionId) => {
      window.removeEventListener("pagehide", leave, true);
      document.removeEventListener("visibilitychange", leave, true);
      resolve(sessionId);
    };
    const leave = (event) => {
      if (event.type === "pagehide" || document.visibilityState === "hidden")
        settle(null);
    };
    window.addEventListener("pagehide", leave, true);
    document.addEventListener("visibilitychange", leave, true);
    pending.then(settle);
  });
}

// Foretell events go through fetch with keepalive, so they outlive the page:
// the wait for the session ID can end just as the page leaves, which would
// cancel axios's request. It carries the headers axios sends and its
// credentials (nuxt.config.js's axios `credentials: true` includes them for API
// requests).
function postWithKeepalive(axios, path, body) {
  return fetch(axios.defaults.baseURL + path, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    headers: { ...axios.defaults.headers.common },
    body: JSON.stringify(body),
  });
}

export default {
  methods: {
    async fireForetellEvent(eventType, payload = {}) {
      payload["event_type"] = eventType;
      if (this.$route.query.utm_source) {
        payload["source"] = this.$route.query.utm_source;
      }
      if (this.$route.query.utm_medium) {
        payload["medium"] = this.$route.query.utm_medium;
      }
      if (this.$route.query.gactid) {
        payload["google_account_id"] = this.$route.query.gactid;
      }
      if (this.$route.query.gcid) {
        payload["google_campaign_id"] = this.$route.query.gcid;
      }
      if (this.$route.query.gagid) {
        payload["google_ad_group_id"] = this.$route.query.gagid;
      }
      if (this.$route.query.gtid) {
        payload["google_target_id"] = this.$route.query.gtid;
      }
      if (this.$route.query.gd) {
        payload["source_device"] = this.$route.query.gd;
      }
      if (this.$route.query.asrc) {
        payload["assist_source"] = this.$route.query.asrc;
      }
      if (this.$route.query.ssg) {
        payload["source_segment"] = this.$route.query.ssg;
      }
      if (this.$route.query.im_ref) {
        payload["affiliate_click_id"] = this.$route.query.im_ref;
      }
      if (this.$route.query.sharedid) {
        payload["affiliate_shared_id"] = this.$route.query.sharedid;
      }
      if (this.$route.query.irpid) {
        payload["affiliate_partner_id"] = this.$route.query.irpid;
      }
      if (this.$route.query.msclkid) {
        payload["msclkid"] = this.$route.query.msclkid;
      }

      const isPurchaseEvent = PURCHASE_EVENT_TYPES.includes(eventType);
      // Supply the signed-in address for provider-managed enhanced conversions.
      // Skip identity calls when the profile has no email.
      const purchaserEmail = isPurchaseEvent ? this.currentUserEmail() : null;

      this.$nextTick(() => {
        if (eventType == "email_submitted") {
          this.$gtag.set({
            user_data: {
              email: payload.user_email,
            },
          });
          this.$gtag.event("conversion", {
            send_to: [
              "AW-1000718781/kmiGCNC2wZ0DEL2Dl90D",
              "AW-854548199/ZQeYCIXG2tEaEOe9vZcD",
              "AW-17439941124/8vOoCJXHqoAbEITEgfxA",
            ],
          });
        }
        if (isPurchaseEvent) {
          if (purchaserEmail) {
            this.$gtag.set({
              user_data: {
                email: purchaserEmail,
              },
            });
          }
          const conversionParams = {
            send_to: [
              "AW-1000718781/PsSoCNrpv4cYEL2Dl90D",
              "AW-854548199/V0OfCKnIh9AaEOe9vZcD",
              "AW-17439941124/LO_HCK6yqYAbEITEgfxA",
            ],
            value: payload.score,
          };
          // Only sent when the caller supplied them, so a payload built
          // without a verified conversion object still fires the event.
          if (payload.transaction_id !== undefined) {
            conversionParams.transaction_id = payload.transaction_id;
          }
          if (payload.currency !== undefined) {
            conversionParams.currency = payload.currency;
          }
          if (payload.new_customer !== undefined) {
            conversionParams.new_customer = payload.new_customer;
          }
          this.$gtag.event("conversion", conversionParams);
        }
        this.$gtag.event(eventType, {
          event_category: "Foretell",
        });
      });
      window.uetq = window.uetq || [];
      if (purchaserEmail) {
        window.uetq.push("set", { pid: { em: purchaserEmail } });
      }
      window.uetq.push("event", eventType, {
        revenue_value: payload.score,
        currency: "USD",
      });
      if (isPurchaseEvent) {
        window.uetq.push("event", "universal_conversion", {
          revenue_value: payload.score,
          currency: "USD",
        });
      }
      // Stripe owns purchase money; the browser still supplies the purchase's
      // marketing context. Match the verified invoiceable's spine marker and
      // keep the composed ad value out of the first-party event.
      const foretellPayload = { ...payload };
      const foretellEventType = isPurchaseEvent
        ? payload.attribution_event_type || eventType
        : eventType;
      if (isPurchaseEvent) {
        for (const key of [
          "score",
          "transaction_id",
          "currency",
          "new_customer",
          "attribution_event_type",
        ]) {
          delete foretellPayload[key];
        }
        foretellPayload.event_type = foretellEventType;
      }
      // The Hindsight session this event belongs to (plugins/hindsight.js):
      // null when capture is off on this page, and never more than 2 seconds.
      // If the page goes first, the event is sent at once without it.
      const sessionId = this.$hindsightSessionId
        ? await sessionIdUnlessLeaving(this.$hindsightSessionId())
        : null;
      if (sessionId) foretellPayload.session_id = sessionId;

      this.$axios.setHeader("Access-Control-Allow-Origin", "*");
      try {
        const response = await postWithKeepalive(
          this.$axios,
          "/foretell_events/api_create",
          { event_type: foretellEventType, event: foretellPayload }
        );
        // axios rejected an error status; fetch resolves it.
        if (!response.ok) console.error(`Foretell event: HTTP ${response.status}`);
      } catch (err) {
        console.error(err);
      }
    },

    // Same source the checkout forms and `email_submitted` use: the VuexORM
    // profile entity, which `handlePaymentIntentCheck` refreshes via
    // `User.getProfile()` before any purchase event fires.
    currentUserEmail() {
      const user = User.current;
      return (user && user.email) || null;
    },

    async createOrLoadUserWithForetellEvent(eventType, payload = {}) {
      if (process.client && !this.$store.state.user.data.id) {
        let params = {
          create: true,
          utm_source: this.$route.query.utm_source,
          utm_medium: this.$route.query.utm_medium,
          utm_campaign: this.$route.query.utm_campaign,
          utm_term: this.$route.query.utm_term,
          network: this.$route.query.network,
          ct: this.$route.query.ct,
          px: this.$route.query.px,
          ft: this.$route.query.ft,
        };
        await this.$store.dispatch("user/getUser", params);
      }

      if (payload.lander_test) {
        payload.lander_test = "n2:" + payload.lander_test;
      }
      this.fireForetellEvent(eventType, payload);
    },
  },
};
